import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  DataTable,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Spinner,
  EmptyState,
  Divider,
  Collapsible
} from '@shopify/polaris';
import { adminApi } from '../../services/api/adminApi';

// Common timezones list
const TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'America/New_York (Eastern)', value: 'America/New_York' },
  { label: 'America/Chicago (Central)', value: 'America/Chicago' },
  { label: 'America/Denver (Mountain)', value: 'America/Denver' },
  { label: 'America/Los_Angeles (Pacific)', value: 'America/Los_Angeles' },
  { label: 'America/Phoenix (MST)', value: 'America/Phoenix' },
  { label: 'America/Anchorage (Alaska)', value: 'America/Anchorage' },
  { label: 'Pacific/Honolulu (Hawaii)', value: 'Pacific/Honolulu' },
  { label: 'Europe/London (GMT)', value: 'Europe/London' },
  { label: 'Europe/Paris (CET)', value: 'Europe/Paris' },
  { label: 'Europe/Berlin (CET)', value: 'Europe/Berlin' },
  { label: 'Europe/Madrid (CET)', value: 'Europe/Madrid' },
  { label: 'Europe/Rome (CET)', value: 'Europe/Rome' },
  { label: 'Europe/Amsterdam (CET)', value: 'Europe/Amsterdam' },
  { label: 'Europe/Stockholm (CET)', value: 'Europe/Stockholm' },
  { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Asia/Hong_Kong (HKT)', value: 'Asia/Hong_Kong' },
  { label: 'Asia/Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'Australia/Sydney (AEST)', value: 'Australia/Sydney' },
  { label: 'Australia/Melbourne (AEST)', value: 'Australia/Melbourne' },
  { label: 'Australia/Brisbane (AEST)', value: 'Australia/Brisbane' },
  { label: 'America/Toronto (Eastern)', value: 'America/Toronto' },
  { label: 'America/Vancouver (Pacific)', value: 'America/Vancouver' },
  { label: 'America/Mexico_City (CST)', value: 'America/Mexico_City' },
  { label: 'America/Sao_Paulo (BRT)', value: 'America/Sao_Paulo' },
  { label: 'America/Buenos_Aires (ART)', value: 'America/Buenos_Aires' }
];

export const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({ status: 'all', role: 'all' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    status: 'active',
    timezone: 'UTC'
  });
  const [accessFormData, setAccessFormData] = useState({
    tenantId: '',
    role: 'user'
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getUsers({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (response.success) {
        setUsers(response.users || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch tenants for access management
  const fetchTenants = useCallback(async () => {
    try {
      const response = await adminApi.getTenants({ status: 'active', limit: 1000 });
      if (response.success) {
        setTenants(response.tenants || []);
      }
    } catch (err) {
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (accessModalOpen) {
      fetchTenants();
    }
  }, [accessModalOpen, fetchTenants]);

  const handleCreate = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'user',
      status: 'active',
      timezone: 'UTC'
    });
    setSelectedUser(null);
    setCreateModalOpen(true);
  };

  const handleEdit = (user) => {
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      status: user.status || 'active',
      timezone: user.timezone || 'UTC'
    });
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleManageAccess = (user) => {
    setSelectedUser(user);
    setAccessFormData({ tenantId: '', role: 'user' });
    setAccessModalOpen(true);
  };

  const handleSubmitCreate = async () => {
    try {
      setSubmitting(true);
      const response = await adminApi.createUser(formData);
      if (response.success) {
        setCreateModalOpen(false);
        fetchUsers();
      } else {
        setError(response.error || 'Failed to create user');
      }
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    try {
      setSubmitting(true);
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      const response = await adminApi.updateUser(selectedUser.id, updateData);
      if (response.success) {
        setEditModalOpen(false);
        fetchUsers();
      } else {
        setError(response.error || 'Failed to update user');
      }
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await adminApi.deleteUser(selectedUser.id);
      if (response.success) {
        setDeleteModalOpen(false);
        fetchUsers();
      } else {
        setError(response.error || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!accessFormData.tenantId) {
      setError('Please select a tenant');
      return;
    }
    try {
      setSubmitting(true);
      const response = await adminApi.grantTenantAccess(
        selectedUser.id,
        accessFormData.tenantId,
        accessFormData.role
      );
      if (response.success) {
        setAccessFormData({ tenantId: '', role: 'user' });
        fetchUsers();
        // Refresh selected user to get updated tenant list
        const updatedResponse = await adminApi.getUsers({ 
          status: filters.status, 
          role: filters.role,
          page: pagination.page,
          limit: pagination.limit 
        });
        if (updatedResponse.success) {
          const updatedUser = updatedResponse.users.find(u => u.id === selectedUser.id);
          if (updatedUser) {
            setSelectedUser(updatedUser);
          }
        }
      } else {
        setError(response.error || 'Failed to grant access');
      }
    } catch (err) {
      setError(err.message || 'Failed to grant access');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeAccess = async (tenantId) => {
    try {
      setSubmitting(true);
      const response = await adminApi.revokeTenantAccess(
        selectedUser.id,
        tenantId
      );
      if (response.success) {
        fetchUsers();
        // Refresh selected user to get updated tenant list
        const updatedResponse = await adminApi.getUsers({ 
          status: filters.status, 
          role: filters.role,
          page: pagination.page,
          limit: pagination.limit 
        });
        if (updatedResponse.success) {
          const updatedUser = updatedResponse.users.find(u => u.id === selectedUser.id);
          if (updatedUser) {
            setSelectedUser(updatedUser);
          }
        }
      } else {
        setError(response.error || 'Failed to revoke access');
      }
    } catch (err) {
      setError(err.message || 'Failed to revoke access');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <Badge status="success">Active</Badge>
    ) : (
      <Badge status="critical">Inactive</Badge>
    );
  };

  const toggleRow = (userId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      super_admin: { tone: 'critical', children: 'Super Admin' },
      admin: { tone: 'warning', children: 'Admin' },
      user: { tone: 'info', children: 'User' }
    };
    return <Badge {...(roleMap[role] || { children: role })} />;
  };

  const getTenantRoleBadge = (role) => {
    const roleMap = {
      owner: { tone: 'success', children: 'Owner' },
      admin: { tone: 'info', children: 'Admin' },
      user: { tone: 'default', children: 'User' },
      viewer: { tone: 'subdued', children: 'Viewer' }
    };
    return <Badge {...(roleMap[role] || { tone: 'default', children: role })} />;
  };

  const getEnvironmentBadge = (env) => {
    const envMap = {
      dev: { tone: 'info', children: 'Dev' },
      staging: { tone: 'warning', children: 'Staging' },
      prod: { tone: 'success', children: 'Prod' }
    };
    return <Badge {...(envMap[env] || { tone: 'default', children: env })} />;
  };

  const tableRows = users.map(user => {
    const isExpanded = expandedRows.has(user.id);
    return [
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Button 
          plain 
          onClick={() => toggleRow(user.id)}
          ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '▼' : '▶'}
        </Button>
        {user.name}
      </div>,
      user.email,
      getRoleBadge(user.role),
      getStatusBadge(user.status),
      user.tenantCount.toString(),
      <div style={{ display: 'flex', gap: '12px' }}>
        <Button size="micro" onClick={() => handleEdit(user)}>Edit</Button>
        <Button size="micro" onClick={() => handleManageAccess(user)}>Manage Access</Button>
        <Button size="micro" destructive onClick={() => handleDelete(user)}>Delete</Button>
      </div>
    ];
  });

  const tableHeadings = [
    'Name',
    'Email',
    'Role',
    'Status',
    'Tenants',
    'Actions'
  ];

  return (
    <BlockStack gap="loose">
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Card>
        <BlockStack gap="loose">
          <InlineStack align="space-between">
            <Text variant="headingMd" fontWeight="bold">Users</Text>
            <Button primary onClick={handleCreate}>Create User</Button>
          </InlineStack>

          <InlineStack gap="loose" align="start">
            <div style={{ minWidth: '200px', width: '200px', marginRight: '24px' }}>
              <Select
                label="Status"
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' }
                ]}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
              />
            </div>
            <div style={{ minWidth: '200px', width: '200px' }}>
              <Select
                label="Role"
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'User', value: 'user' },
                  { label: 'Admin', value: 'admin' },
                  { label: 'Super Admin', value: 'super_admin' }
                ]}
                value={filters.role}
                onChange={(value) => setFilters({ ...filters, role: value })}
              />
            </div>
          </InlineStack>

          {loading ? (
            <BlockStack align="center">
              <Spinner size="large" />
            </BlockStack>
          ) : users.length === 0 ? (
            <EmptyState
              heading="No users found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Create your first user to get started.</p>
            </EmptyState>
          ) : (
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                    {tableHeadings.map((heading, idx) => (
                      <th
                        key={idx}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#202223'
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => {
                    const isExpanded = expandedRows.has(user.id);
                    return (
                      <React.Fragment key={user.id}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e1e3e5',
                            backgroundColor: isExpanded ? '#f6f6f7' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Button
                                plain
                                onClick={() => toggleRow(user.id)}
                                ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? '▼' : '▶'}
                              </Button>
                              {user.name}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>{user.email}</td>
                          <td style={{ padding: '12px 16px' }}>{getRoleBadge(user.role)}</td>
                          <td style={{ padding: '12px 16px' }}>{getStatusBadge(user.status)}</td>
                          <td style={{ padding: '12px 16px' }}>{user.tenantCount.toString()}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <Button size="micro" onClick={() => handleEdit(user)}>Edit</Button>
                              <Button size="micro" onClick={() => handleManageAccess(user)}>Manage Access</Button>
                              <Button size="micro" destructive onClick={() => handleDelete(user)}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && user.tenants && user.tenants.length > 0 && (
                          <tr>
                            <td colSpan={6} style={{ padding: '0', borderBottom: '1px solid #e1e3e5' }}>
                              <Collapsible
                                open={isExpanded}
                                id={`tenant-list-${user.id}`}
                                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                              >
                                <div style={{ 
                                  padding: '16px 48px',
                                  backgroundColor: '#fafbfb',
                                  borderLeft: '3px solid #008060'
                                }}>
                                  <BlockStack gap="base">
                                    <Text variant="headingSm" fontWeight="semibold" color="subdued">
                                      Tenant Access ({user.tenants.length})
                                    </Text>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                      gap: '12px'
                                    }}>
                                      {user.tenants.map(tenant => (
                                        <Card key={tenant.id} background="bg-surface-secondary">
                                          <BlockStack gap="tight">
                                            <InlineStack align="space-between" blockAlign="start">
                                              <BlockStack gap="extraTight">
                                                <Text fontWeight="semibold">{tenant.name}</Text>
                                                <InlineStack gap="tight" blockAlign="center">
                                                  {getEnvironmentBadge(tenant.environment)}
                                                  {getTenantRoleBadge(tenant.role)}
                                                </InlineStack>
                                              </BlockStack>
                                            </InlineStack>
                                          </BlockStack>
                                        </Card>
                                      ))}
                                    </div>
                                  </BlockStack>
                                </div>
                              </Collapsible>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </BlockStack>
      </Card>

      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create User"
        primaryAction={{
          content: 'Create',
          onAction: handleSubmitCreate,
          loading: submitting
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setCreateModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            <TextField
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              autoComplete="off"
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(value) => setFormData({ ...formData, password: value })}
              autoComplete="off"
            />
            <Select
              label="Role"
              options={[
                { label: 'User', value: 'user' },
                { label: 'Admin', value: 'admin' },
                { label: 'Super Admin', value: 'super_admin' }
              ]}
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
            />
            <Select
              label="Timezone"
              options={TIMEZONES}
              value={formData.timezone}
              onChange={(value) => setFormData({ ...formData, timezone: value })}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit User"
        primaryAction={{
          content: 'Save',
          onAction: handleSubmitEdit,
          loading: submitting
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            <TextField
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(value) => setFormData({ ...formData, password: value })}
              helpText="Leave blank to keep current password"
              autoComplete="off"
            />
            <Select
              label="Role"
              options={[
                { label: 'User', value: 'user' },
                { label: 'Admin', value: 'admin' },
                { label: 'Super Admin', value: 'super_admin' }
              ]}
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
            />
            <Select
              label="Status"
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' }
              ]}
              value={formData.status || 'active'}
              onChange={(value) => setFormData({ ...formData, status: value })}
            />
            <Select
              label="Timezone"
              options={TIMEZONES}
              value={formData.timezone}
              onChange={(value) => setFormData({ ...formData, timezone: value })}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
        primaryAction={{
          content: 'Delete',
          onAction: handleConfirmDelete,
          loading: submitting,
          destructive: true
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <Text>
            Are you sure you want to delete user "{selectedUser?.name}"? This action will deactivate the user.
          </Text>
        </Modal.Section>
      </Modal>

      {/* Access Management Modal */}
      <Modal
        open={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        title={`Manage Access - ${selectedUser?.name}`}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setAccessModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            {/* Grant Access Section */}
            <BlockStack gap="base">
              <Text variant="headingSm" fontWeight="bold">Grant Access</Text>
              <Select
                label="Tenant"
                options={[
                  { label: 'Select tenant...', value: '' },
                  ...tenants
                    .filter(t => {
                      // Filter out tenants user already has access to
                      if (!selectedUser?.tenants) return true;
                      return !selectedUser.tenants.some(ut => ut.id === t.id);
                    })
                    .map(t => ({ label: `${t.name} (${t.environment})`, value: t.id }))
                ]}
                value={accessFormData.tenantId}
                onChange={(value) => setAccessFormData({ ...accessFormData, tenantId: value })}
              />
              <Select
                label="Role"
                options={[
                  { label: 'User', value: 'user' },
                  { label: 'Admin', value: 'admin' },
                  { label: 'Viewer', value: 'viewer' }
                ]}
                value={accessFormData.role}
                onChange={(value) => setAccessFormData({ ...accessFormData, role: value })}
              />
              <div style={{ paddingTop: '8px', paddingBottom: '16px' }}>
                <Button onClick={handleGrantAccess} loading={submitting}>
                  Grant Access
                </Button>
              </div>
            </BlockStack>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Divider />
            </div>

            {/* Current Access Section */}
            <BlockStack gap="base">
              <Text variant="headingSm" fontWeight="bold">Current Access</Text>
              {selectedUser?.tenants && selectedUser.tenants.length > 0 ? (
                <BlockStack gap="base">
                  {selectedUser.tenants.map(tenant => (
                    <div key={tenant.id} style={{ padding: '8px 0' }}>
                      <InlineStack align="space-between" blockAlign="center">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text fontWeight="medium">{tenant.name}</Text>
                          {getEnvironmentBadge(tenant.environment)}
                          <Text>—</Text>
                          {getTenantRoleBadge(tenant.role)}
                        </div>
                        {tenant.role !== 'owner' && (
                          <Button 
                            size="micro" 
                            destructive 
                            onClick={() => handleRevokeAccess(tenant.id)}
                            loading={submitting}
                          >
                            Revoke
                          </Button>
                        )}
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              ) : (
                <Text tone="subdued">No tenant access granted yet.</Text>
              )}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
};
